import styled from "styled-components";
import { Spacer } from "../Spacer";
import { dateFormatterWeekDayDayMonthYear } from "../../../format";
import { TextCapitalized } from "../text/TextCapitalized";
import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { TripsAndPlanningsVehicleCard } from "../TripsAndPlanningsVehicleCard";
import "../../../../i18n/i18n";
import { ClearRound } from "../../materialUi/materialUi";
import { HoursBarTrips } from "./HoursBarTrips";

export type VehicleType = "SHARED" | "PERSONAL" | "OUT_OF_FLEET";

export type DrivingEvent = {
  startDate: string;
  endDate: string;
  eventType: string;
  unit: string | null;
  value: number | null;
};

export interface Trip {
  startDate: string;
  endDate: string;
  distance: number | null;
  ecoDrivingScore: number | null;
  smoothnessScore: number | null;
  harshAccelerationsCount: number | null;
  harshBrakesCount: number | null;
  sharpTurnsCount: number | null;
}

export interface TripOrPlanningOptionsDisplay {
  distance: number | null;
  duration: number | null;
  startDate: string;
  endDate: string;
  ecoDrivingScore?: number | null;
  harshAccelerationsCount: number | null;
  harshBrakesCount: number | null;
  sharpTurnsCount: number | null;
  driver: string | null;
  clientName?: string | null;
  clientCity?: string | null;
  hasKmClient?: boolean;
  activityType?: string | null;
  isOutIntervention?: boolean;
}

export interface DriverInfo {
  name: string;
  city: string | null;
}

export interface VehicleInfo {
  registrationNumber: string | null;
  reference: string | null;
  vehicleType: VehicleType;
  model: string | null;
}

export interface PlanningInfo {
  startTime: string;
  endTime: string;
  expectedMileage: number;
  clientName: string | null;
  clientCity: string | null;
  hasKmClient: boolean;
  activityType?: string | null;
  isOutIntervention?: boolean;
}

export interface DayTripOrPlanningDetailsProps {
  date: Date | null;
  vehicle: VehicleInfo | null;
  trips: Trip[];
  driver: DriverInfo | null;
  onClose: () => void;
  totalDistance: number;
  plannings: PlanningInfo[];
  planningTotalDistance: number;
  transactionsAndAuth: TransactionAndAuthorizationOnTrip[];
}
export interface TransactionAndAuthorizationOnTrip {
  date: string;
}

const removeSecondsFromDate = (date: string): string => {
  return new Date(
    new Date(date).setHours(
      new Date(date).getHours(),
      new Date(date).getMinutes(),
      0,
      0,
    ),
  ).toISOString();
};

const getAllTransactionDisplayedFilteredDuplicateIn20Minutes = (
  transactionsAndAuthDates: TransactionAndAuthorizationOnTrip[],
) => {
  if (transactionsAndAuthDates.length === 0) return [];
  const sortedDates = transactionsAndAuthDates.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  return sortedDates.reduce(
    (
      acc: TransactionAndAuthorizationOnTrip[],
      current: TransactionAndAuthorizationOnTrip,
    ) => {
      if (
        acc.length === 0 ||
        (new Date(current.date).getTime() -
          new Date(acc[acc.length - 1].date).getTime()) /
          (1000 * 60) >=
          20
      ) {
        acc.push(current);
      }
      return acc;
    },
    [],
  );
};

export const DayTripDetails = (props: DayTripOrPlanningDetailsProps) => {
  const {
    date,
    trips,
    vehicle,
    totalDistance,
    driver,
    plannings,
    planningTotalDistance,
    transactionsAndAuth,
  } = props;
  const transactionsToDisplay =
    getAllTransactionDisplayedFilteredDuplicateIn20Minutes(transactionsAndAuth);

  const sortTrips = [...trips]
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )
    .map((t) => {
      return {
        ...t,
        startDate: removeSecondsFromDate(t.startDate),
        endDate: removeSecondsFromDate(t.endDate),
      };
    });

  const sortPlannings = [...plannings]
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )
    .map((p) => {
      return {
        ...p,
        startTime: removeSecondsFromDate(p.startTime),
        endTime: removeSecondsFromDate(p.endTime),
        isOutIntervention: !p.clientCity && !p.clientName,
      };
    });

  sortPlannings.forEach((planning, index) => {
    if (index > 0) {
      if (planning.hasKmClient === true)
        sortPlannings[index - 1].hasKmClient = true;
    }
  });

  return (
    <StyledDayTripOrPlanningDetailsContainer>
      <Spacer y={1.5} />
      <StyledFlex>
        <Spacer x={1.5} />
        <StyledColumnTitle>
          <Spacer y={0.5} />
          <TextCapitalized>
            {date && dateFormatterWeekDayDayMonthYear(date)}
          </TextCapitalized>
          <Spacer y={1.5} />
          {vehicle && (
            <StyledVehicleCard>
              <TripsAndPlanningsVehicleCard
                registrationNumber={vehicle.registrationNumber}
                totalDistance={totalDistance}
                driverCity={driver?.city ?? null}
                type="TRIP"
              />
              {sortPlannings.length > 0 && (
                <>
                  <Spacer y={1} />
                  <TripsAndPlanningsVehicleCard
                    registrationNumber={null}
                    totalDistance={planningTotalDistance}
                    type="PLANNING"
                  />
                </>
              )}
            </StyledVehicleCard>
          )}
        </StyledColumnTitle>
        <Spacer x={1.5} />
        {sortTrips.length !== 0 && (
          <HoursBarTrips
            trips={sortTrips}
            driverName={driver?.name ?? null}
            plannings={sortPlannings}
            transactionsToDisplay={transactionsToDisplay}
          />
        )}
        <Spacer x={1} />
        <StyledAroundCloseIcon onClick={props.onClose}>
          <ClearRound />
        </StyledAroundCloseIcon>
        <Spacer x={1.5} />
      </StyledFlex>
      <Spacer y={1.5} />
    </StyledDayTripOrPlanningDetailsContainer>
  );
};

const StyledAroundCloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1.5rem;
  cursor: pointer;
  min-width: 2.5rem;
  height: 2.5rem;
  &:hover {
    background-color: ${colors["colors/surfaces/background/background_level1"]};
  }
  & svg {
    width: 1.75rem;
    height: 1.75rem;
    fill: ${colors["colors/text/darkGrey"]};
  }
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledVehicleCard = styled.div`
  width: fit-content;
  min-width: 15rem;
`;

const StyledColumnTitle = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Header/H2"]};
  width: fit-content;
  white-space: nowrap;
`;

const StyledDayTripOrPlanningDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
  ${typographies["Body/S"]};
  border-radius: 0.5rem;
  border: 0.0625rem solid ${colors["colors/borders/input/default"]};
  overflow: hidden;
`;
